import { useEffect, useState } from 'react';
import type { paths } from '../generated/api.ts';
import { chatStreamUpdateSchema, runProgressEventSchema } from '../shared/chats.ts';
import { queryClient } from './api.ts';

type Chat = paths['/api/chats/{chatId}']['get']['responses'][200]['content']['application/json'];

export function useChatStream(chatId: string | undefined, runId: string | undefined, working: boolean) {
  const [reconnecting, setReconnecting] = useState(false);
  const [progress, setProgress] = useState<{
    runId: string;
    activity: string;
    message: { itemId: string; text: string } | null;
    reasoning: { itemId: string; summary: Record<number, { type: 'summary_text'; text: string }> } | null;
  }>();
  useEffect(() => {
    if (!chatId || !runId || !working) return;
    let source: EventSource;
    let retry: ReturnType<typeof setTimeout> | undefined;
    let retryDelay = 1000;
    const connect = () => {
      source = new EventSource(`/api/runs/${runId}/stream`);
      source.onopen = () => {
        setReconnecting(false);
        setProgress({ runId, activity: 'Working', message: null, reasoning: null });
        retryDelay = 1000;
      };
      source.addEventListener('progress', (event: MessageEvent<string>) => {
        const update = runProgressEventSchema.parse(JSON.parse(event.data));
        setProgress((current) => {
          const state =
            current?.runId === runId
              ? current
              : { runId, activity: 'Working', message: null, reasoning: null };
          if (update.type === 'activity') return { ...state, activity: update.activity };
          if (update.type === 'reasoning') {
            const current = state.reasoning?.itemId === update.itemId ? state.reasoning.summary : {};
            const part = current[update.summaryIndex];
            if (!update.start && !part) return state;
            return {
              ...state,
              reasoning: {
                itemId: update.itemId,
                summary: {
                  ...current,
                  [update.summaryIndex]: {
                    type: 'summary_text' as const,
                    text: update.start ? update.delta : part!.text + update.delta,
                  },
                },
              },
            };
          }
          // A connection that missed the beginning waits for the saved, complete message.
          const message = update.start
            ? { itemId: update.itemId, text: update.delta }
            : state.message?.itemId === update.itemId
              ? { ...state.message, text: state.message.text + update.delta }
              : null;
          return { ...state, activity: 'Writing', message };
        });
      });
      source.onerror = () => {
        setReconnecting(true);
        // EventSource retries a dropped connection itself, but HTTP errors can close it permanently.
        if (source.readyState === EventSource.CLOSED) {
          retry = setTimeout(connect, retryDelay);
          retryDelay = Math.min(retryDelay * 2, 10_000);
        }
      };
      source.addEventListener('update', (event: MessageEvent<string>) => {
        const { run, messages } = chatStreamUpdateSchema.parse(JSON.parse(event.data));
        queryClient.setQueryData<Chat>(['chat', chatId], (current) => {
          if (!current) return current;
          const saved = new Set(current.messages.map((message) => message.id));
          return {
            ...current,
            messages: [...current.messages, ...messages.filter((message) => !saved.has(message.id))],
            runs: current.runs.map((existing) =>
              existing.id === run.id ? { ...existing, ...run } : existing,
            ),
          };
        });
        if (run.status !== 'Running') source.close();
      });
    };
    connect();
    return () => {
      clearTimeout(retry);
      source.close();
    };
  }, [chatId, runId, working]);
  return {
    reconnecting: working && reconnecting,
    progress: progress?.runId === runId ? progress : undefined,
  };
}
